import { Dialog, makeStyles, CircularProgress } from '@material-ui/core';
import React from 'react';

import { Alert, Button, Header, Select } from 'src/common/ui/components';
import useTypedSWR from 'src/components/general/useTypedSWR';
import { SEND_DENIAL_NOTICE } from 'src/idVerification/mutations/sendDenialNotice.gql';
import { GET_DENIAL_REASONS } from 'src/idVerification/queries/getDenialReasons.gql';
import useApolloClient from 'src/shared/client/useApolloClient';

type SendDenialNoticeWidgetProps = {
  patientId: string;
};

type DenialReasonsResponse = {
  staff_idVerificationDenialReasons: string[];
};

export const SendDenialNoticeWidget = ({ patientId }: SendDenialNoticeWidgetProps) => {
  const styles = useStyles();
  const apolloClient = useApolloClient();

  const [showModal, setShowModal] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState<string | null>(null);
  const [isSending, setIsSending] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const { data: denialReasonsData, error: denialReasonsError } =
    useTypedSWR<DenialReasonsResponse>(GET_DENIAL_REASONS);

  const handleSelectOption = (_evt: React.SyntheticEvent, val: string) => {
    setSelectedOption(val);
  };

  const handleSendNotice = async () => {
    if (!apolloClient || !selectedOption) {
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      await apolloClient.mutate({
        mutation: SEND_DENIAL_NOTICE,
        variables: {
          patientId,
          reason: selectedOption,
        },
      });
      handleClose();
    } catch (e) {
      setError(e);
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setSelectedOption(null);
    setShowModal(false);
    setError(null);
    setIsSending(false);
  };

  return (
    <>
      <Button onClick={() => setShowModal(true)}>Send Patient Notice</Button>
      <Dialog open={showModal} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
        <div className={styles.dialogContent}>
          <Header>Reason for Denial</Header>
          {denialReasonsError && (
            <Alert
              title="Error loading denial reasons"
              error={denialReasonsError}
              severity="error"
            />
          )}
          {!denialReasonsError && !denialReasonsData && <CircularProgress size={20} />}
          {!denialReasonsError && denialReasonsData && (
            <>
              <Select
                options={denialReasonsData.staff_idVerificationDenialReasons}
                label="Select Option"
                onChange={handleSelectOption}
                openOnFocus
              />
              {error && (
                <Alert
                  title="Failed to send notice. Please try again."
                  error={error}
                  severity="error"
                />
              )}
              <div className={styles.buttonContainer}>
                <Button disabled={!selectedOption || isSending} onClick={handleSendNotice}>
                  {isSending ? 'Sending...' : 'Send Notice'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '20px',
    height: '500px',
    overflowY: 'auto',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    marginTop: '10px',
  },
});
